import { format } from "date-fns";
import { ISchedule } from "@gettactic/api";
import { AgendaCardLayout } from "@gettactic/components/src/components/reservation-card/AgendaCardLayout";
import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { useAreas, useAuthenticated } from "@gettactic/hooks";
import { AVATAR_SIZES } from "@cmp-src/components/UserAvatar/UserAvatar";
import { cn, findCheckinTargetSlot } from "@gettactic/helpers";
import { ResourceTypes } from "@gettactic/helpers/src/resources/utils";

interface ScheduleInfoCardProps {
  schedule: ISchedule;
  className?: string;
  innerClassName?: string;
}

export function ScheduleInfoCard({
  schedule,
  className,
  innerClassName
}: ScheduleInfoCardProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { areas: areaList } = useAreas(schedule?.resource?.office_id ?? null);

  if (!schedule?.resource?.office_id) {
    return null;
  }

  const startDate = new Date(schedule.start);
  const endDate = new Date(schedule.end);

  const formatTime = (date: Date) => format(date, "HH:mm");
  const formatDate = (date: Date) => format(date, "EEE, MMM d");

  const participants = schedule.slots.map((slot) => ({
    id: slot.user.id,
    name: slot.user.name,
    email: slot.user.email,
    profile_photo_url: slot.user.profile_photo_url,
    roles: slot.user.roles || []
  }));

  const office = authenticatedUser.offices?.byId[schedule.resource.office_id];
  const officeName = office?.name || "Unknown Office";

  const getResourceTypeName = (type: string) => {
    switch (type) {
      case ResourceTypes.desk:
        return "Desk";
      case ResourceTypes.workspace:
        return "Workspace";
      case ResourceTypes.meeting_room:
        return "Meeting Room";
      case ResourceTypes.parking_space:
        return "Parking Space";
      case ResourceTypes.parking_lot:
        return "Parking Lot";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const targetSlot = findCheckinTargetSlot(
    schedule,
    authenticatedUser.user?.id ?? ""
  );

  const location =
    !!targetSlot && schedule.resource.parent_id ? (
      <ResourceLocation
        location={officeName}
        floor={areaList?.byId[schedule.resource.parent_id]?.name}
        area={areaList?.byId[schedule.resource.id]?.name}
      />
    ) : null;

  const isMeetingRoom = schedule.resource.type === "meeting_room";

  return (
    <AgendaCardLayout
      className={cn(className, "mx-0 px-0 md:m-0")}
      innerClassName={innerClassName}
      topLeft={
        <div className="text-gray-900">
          <span className="font-semibold">{formatDate(startDate)}</span>
          <span className="text-gray-500">
            {" "}
            • {formatTime(startDate)} - {formatTime(endDate)}
          </span>
          <div>
            {getResourceTypeName(schedule.resource.type)}{" "}
            {schedule.resource.name}
          </div>
        </div>
      }
      bottomLeft={
        <div className="flex flex-col space-y-2">
          {isMeetingRoom && (
            <div className="flex flex-col space-y-1">{location}</div>
          )}
        </div>
      }
      bottomRight={
        isMeetingRoom ? (
          <div className="flex flex-col space-y-1">
            <UserAvatarStack
              users={participants}
              avatarSize={AVATAR_SIZES.ROUNDED_SM}
            />
          </div>
        ) : (
          !!location && (
            <div className="flex w-full flex-col justify-start space-y-1">
              {location}
            </div>
          )
        )
      }
    />
  );
}
